import * as React from 'react';
import { ClientItemId } from 'owa-client-ids';
import { FreeBusyType } from 'owa-freebusy-style';
import { AgendaFreeBusyBar } from './AgendaFreeBusyBar';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { Detail, TextDetailPart, isButtonDetailPart } from '../schema/DetailPart';
import { DetailPartView } from './AgendaEventDetailViews';
import { IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { IconWell } from './IconWell';
import { FocusZone, FocusZoneDirection, IFocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { KeyboardEvent } from 'react';
import { getRTLSafeKeyCode } from '@uifabric/utilities';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';

const styles = require('./AgendaEventViewBase.scss');
const classNames = require('classnames/bind').bind(styles);

export interface AgendaEventViewBaseProps {
    id: ClientItemId;
    onClick: (target: any) => void;
    onDoubleClick: (target: any) => void;
    onRightClick: (target: any) => void;
    className?: string;
    timeDetails: TextDetailPart[];
    details: Detail[];
    freeBusyType: FreeBusyType;
    colorScheme: ColorScheme;
    icons: IIconProps[];
    isSelected: boolean;
}

export class AgendaEventViewBase extends React.Component<AgendaEventViewBaseProps, {}> {
    private focusZoneRef: React.RefObject<IFocusZone> = React.createRef();

    render() {
        const {
            className,
            timeDetails,
            details,
            icons,
            freeBusyType,
            colorScheme,
            isSelected,
        } = this.props;
        return (
            <FocusZone
                direction={FocusZoneDirection.horizontal}
                componentRef={this.focusZoneRef}
                data-is-focusable={true}
                onClick={this.onClick}
                onDoubleClick={this.onDoubleClick}
                onContextMenu={this.onRightClick}
                className={classNames(
                    styles.agendaItem,
                    {
                        selected: isSelected,
                    },
                    className
                )}
                onKeyDown={this.onKeyDown}>
                <AgendaFreeBusyBar color={colorScheme} freeBusyType={freeBusyType} />
                <div className={styles.base}>{this.getDetailRows(details, timeDetails, icons)}</div>
            </FocusZone>
        );
    }

    private getDetailRows = (
        details: Detail[],
        timeDetails: TextDetailPart[],
        icons: IIconProps[]
    ): JSX.Element[] => {
        const longerDetailsLength = Math.max(details.length, timeDetails.length);
        const detailElements = [];
        /** align the icons with the last detail, unless the button occupies this space, then align the icons with the last time detail
         * (this happens when there is only 1 detail, and a button)
         */
        const iconRowIndex = this.getLastNonButtonIndex(details);
        for (let index = 0; index < longerDetailsLength; index++) {
            detailElements.push(
                <DetailRow
                    key={index}
                    timeDetail={timeDetails.length > index ? timeDetails[index] : null}
                    detail={details.length > index ? details[index] : null}
                    icons={index === iconRowIndex ? icons : null}
                />
            );
        }
        return detailElements;
    };

    private getLastNonButtonIndex(details: Detail[]): number | null {
        for (let index = details.length - 1; index >= 0; index--) {
            if (!isButtonDetailPart(details[index])) {
                return index;
            }
        }
        return null;
    }

    private onKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case getRTLSafeKeyCode(KeyCodes.right):
                this.focusZoneRef.current.focus(true);
                target.preventDefault();
                target.stopPropagation();
                break;
        }
    };

    private onClick = evt => {
        this.props.onClick(evt);
    };

    private onDoubleClick = evt => {
        this.props.onDoubleClick(evt);
    };

    private onRightClick = evt => {
        this.props.onRightClick(evt);
    };
}

const DetailRow = function DetailRow(props: {
    detail: Detail | null;
    timeDetail: TextDetailPart | null;
    icons: IIconProps[] | null;
}) {
    return (
        <div className={styles.detailRow}>
            <div className={styles.timesContainer}>
                {props.timeDetail && (
                    <div
                        title={props.timeDetail.getTextProps().text}
                        className={classNames(
                            props.timeDetail.getTextProps().className,
                            styles.timeDetail
                        )}>
                        {props.timeDetail.getTextProps().text}
                    </div>
                )}
            </div>
            {props.detail && <DetailPartView {...props.detail} />}
            {props.icons && props.icons.length > 0 && <IconWell icons={props.icons} />}
        </div>
    );
};
